import { useReaction } from "@/Shared/Reactions/useReaction";
import { ReactionType } from "@/Shared/Reactions/ReactionType";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { UUID } from "@/Shared/types";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import { SharedDedicated } from "@freightsimple/generated-dashboard-openapi-client/models/SharedDedicated";
import { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookShipmentTwoColumnView } from "./BookShipmentTwoColumnView";
import { BookShipmentDedicatedLogisticsOfferButtonPositionType } from "../Types/BookShipmentDedicatedLogisticsOfferButtonPositionType";
import {
  ConfirmManualQuoteRequestDialogBody_,
  type ConfirmManualQuoteRequestDialogCallbackData,
} from "./Internal/bookShipmentManualQuoteRequestDialogBody";

interface DedicatedLogisticsOfferProps {
  shipmentId: UUID;
  onModify: () => void;
  onRequestManualQuotes: (
    shipmentId: UUID,
    equipmentType: EquipmentType,
    sharedDedicated: SharedDedicated,
    tarpRequired: boolean | undefined,
    linearFeet: number | undefined,
    notes: string,
  ) => Promise<void>;
  lineItems: LineItem[];
  pickupDate: string;
  pickupDeadline: string | undefined;
  pickupLocation: PreQuoteLocation;
  deliveryLocation: PreQuoteLocation;
  deliveryDeadline: string | undefined;
  hideModifyButton?: boolean | undefined;
  buttonPosition: BookShipmentDedicatedLogisticsOfferButtonPositionType;
  equipmentType: EquipmentType | undefined;
  exclusiveUse: boolean;
  tarpRequired: boolean;
  linearFeet: number | undefined;
  pickupReferenceNumber?: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  deliveryAmazonReferenceId?: string | undefined;
  deliveryAmazonShipmentId?: string | undefined;
}

interface LeftSideProps {
  pickupDeadline: string | undefined;
  lineItems: LineItem[];
  pickupDate: string;
  pickupLocation: PreQuoteLocation;
  deliveryDeadline: string | undefined;
  deliveryLocation: PreQuoteLocation;
  equipmentType: EquipmentType | undefined;
  exclusiveUse: boolean;
  tarpRequired: boolean;
  linearFeet: number | undefined;
  pickupReferenceNumber?: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  deliveryAmazonReferenceId?: string | undefined;
  deliveryAmazonShipmentId?: string | undefined;
}

function LeftSide_(props: LeftSideProps) {
  return <BookShipmentTwoColumnView {...props} />;
}

interface RightSideProps {
  onModify: () => void;
  onConfirm: () => void;
  hideModifyButton: boolean | undefined;
  buttonPosition: BookShipmentDedicatedLogisticsOfferButtonPositionType;
}

function RightSide_(props: RightSideProps) {
  return (
    <div className="flex flex-col items-center" style={{ width: "40%" }}>
      <div className="flex flex-col items-start">
        <T size="base" color="secondary">
          For some larger or more complex shipments, the range of instant quotes
          may be limited.
        </T>
        <div className="h-[24px]" />
        <T size="base" color="secondary">
          Our dedicated logistics team will help you get quotes for these more
          difficult shipments.
        </T>
        <div className="h-[24px]" />
        <T size="base" color="secondary">
          Our team will work with our carrier partners to find the best options
          for your shipment, and contact you when they're ready - usually within
          a few hours.
        </T>
      </div>
      <div className="h-[48px]" />
      {props.buttonPosition ===
        BookShipmentDedicatedLogisticsOfferButtonPositionType.Inline && (
        <>
          <Button primary size="large" onClick={props.onConfirm}>
            Request Manual Quotes 💪
          </Button>
          {!props.hideModifyButton && (
            <>
              <div className="h-[8px]" />
              <Button size="link" onClick={props.onModify}>
                Modify Shipment Details
              </Button>
            </>
          )}
        </>
      )}
    </div>
  );
}

export function BookShipmentDedicatedLogisticsOffer(
  props: DedicatedLogisticsOfferProps,
) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Remount key so the request form starts fresh each time it opens.
  const [confirmOpenCount, setConfirmOpenCount] = useState(0);
  const { showReaction } = useReaction();
  const navigate = useNavigate();

  function onConfirm() {
    setConfirmOpenCount((count) => count + 1);
    setConfirmOpen(true);
  }

  async function onManualQuoteRequestFinished(
    result: ConfirmManualQuoteRequestDialogCallbackData,
  ) {
    setConfirmOpen(false);
    await props.onRequestManualQuotes(
      props.shipmentId,
      result.equipmentType,
      result.sharedDedicated,
      result.tarpRequired,
      result.linearFeet,
      result.notes,
    );
    showReaction(ReactionType.Success);
    navigate("/quotes");
  }

  return (
    <div className="flex flex-col items-center">
      <Dialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        width="medium"
        title="Additional Details"
      >
        <ControlPresentationProvider presentation="underlined">
          {confirmOpen && (
            <ConfirmManualQuoteRequestDialogBody_
              key={confirmOpenCount}
              onCancel={() => setConfirmOpen(false)}
              onFinished={onManualQuoteRequestFinished}
              linearFeet={props.linearFeet}
            />
          )}
        </ControlPresentationProvider>
      </Dialog>
      {props.buttonPosition ===
        BookShipmentDedicatedLogisticsOfferButtonPositionType.Top && (
        <>
          <div className="h-[32px]" />
          <Button primary size="large" onClick={onConfirm}>
            Click to Request More Quotes 💪
          </Button>
          {!props.hideModifyButton && (
            <>
              <div className="h-[8px]" />
              <Button size="link" onClick={props.onModify}>
                Modify Shipment Details
              </Button>
            </>
          )}
        </>
      )}
      <div
        className="flex flex-row items-start justify-between"
        style={{ padding: "64px" }}
      >
        <LeftSide_
          pickupDeadline={props.pickupDeadline}
          pickupDate={props.pickupDate}
          lineItems={props.lineItems}
          pickupLocation={props.pickupLocation}
          deliveryLocation={props.deliveryLocation}
          deliveryDeadline={props.deliveryDeadline}
          equipmentType={props.equipmentType}
          exclusiveUse={props.exclusiveUse}
          tarpRequired={props.tarpRequired}
          linearFeet={props.linearFeet}
          pickupReferenceNumber={props.pickupReferenceNumber}
          deliveryReferenceNumber={props.deliveryReferenceNumber}
          deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
          deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
        />
        <RightSide_
          onModify={props.onModify}
          onConfirm={onConfirm}
          hideModifyButton={props.hideModifyButton}
          buttonPosition={props.buttonPosition}
        />
      </div>
    </div>
  );
}
